import { useEffect, useState } from "react";
import { Check, ClipboardList, Mail, Phone, RotateCcw, Users } from "lucide-react";
import { api, fmt } from "../../lib/api";
import { fmtDateTimeMtl } from "../../lib/dateMtl";
import ExpandableText from "./ExpandableText";

const PACKAGES = {
  participation: { label: "Bronze", cls: "text-amber-500 border-amber-500/40 bg-amber-500/10" },
  classique: { label: "Argent", cls: "text-slate-300 border-slate-300/40 bg-slate-300/10" },
  prestige: { label: "Or", cls: "text-gold border-gold/40 bg-gold/10" },
};

// Carte « Soumissions de forfaits » de l'onglet Forfaits : demandes de soumission
// reçues de clients inscrits (page /forfaits) — à cocher « traitée » au fil du suivi.
export default function ForfaitSoumissions() {
  const [items, setItems] = useState([]);
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    api.get("/admin/call-requests")
      .then(({ data }) => setItems((data.requests || []).filter((r) => r.package)))
      .catch(() => {});
  }, []);

  const toggle = async (r, done) => {
    try {
      await api.patch(`/admin/call-requests/${r.id}`, { done });
      setItems((xs) => xs.map((x) => (x.id === r.id ? { ...x, done } : x)));
    } catch { /* silencieux : la liste se rechargera */ }
  };

  const pending = items.filter((r) => !r.done);
  const done = items.filter((r) => r.done);

  const Row = ({ r, isDone }) => {
    const pack = PACKAGES[r.package] || { label: r.package, cls: "text-ivory/70 border-ivory/30 bg-ivory/5" };
    const details = [r.organization, r.city, r.best_time ? `Tournoi : ${r.best_time}` : null]
      .filter(Boolean).join(" · ");
    return (
      <li data-testid={`soumission-${r.id}`}
        className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-xs ${isDone ? "border-ivory/5 opacity-50" : "border-amber-500/25"}`}>
        <button
          onClick={() => toggle(r, !isDone)}
          data-testid={`soumission-done-${r.id}`}
          title={isDone ? "Remettre en attente" : "Marquer « traitée »"}
          className={`mt-0.5 shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
            isDone ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400" : "border-gold/50 text-transparent hover:border-gold"}`}>
          <Check size={13} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-ivory font-semibold flex flex-wrap items-center gap-2">
            {r.name}
            <span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${pack.cls}`}
              data-testid={`soumission-package-${r.id}`}>{pack.label}</span>
          </p>
          {details && <ExpandableText text={details} testId={`soumission-details-${r.id}`} className="block text-ivory/50" />}
          <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
            {r.user_email && (
              <a href={`mailto:${r.user_email}`} data-testid={`soumission-email-${r.id}`}
                className="inline-flex items-center gap-1 text-ivory/60 hover:text-gold transition-colors">
                <Mail size={11} /> {r.user_email}
              </a>
            )}
            <a href={`tel:+${(r.phone || "").replace(/[^0-9]/g, "")}`} data-testid={`soumission-phone-${r.id}`}
              className="inline-flex items-center gap-1 text-amber-300 hover:text-gold font-semibold transition-colors">
              <Phone size={11} /> {r.phone}
            </a>
          </span>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-ivory/60">
            <span className="inline-flex items-center gap-1" data-testid={`soumission-players-${r.id}`}>
              <Users size={11} className="text-gold/70" /> {r.players} joueurs
            </span>
            {r.estimated_total > 0 && (
              <strong className="text-champagne" data-testid={`soumission-total-${r.id}`}>{fmt(r.estimated_total)} estimés</strong>
            )}
          </span>
          {r.message && <ExpandableText text={r.message} testId={`soumission-message-${r.id}`} className="block text-ivory/45 italic mt-0.5" />}
        </div>
        <span className="text-ivory/40 shrink-0">{fmtDateTimeMtl(r.created_at)}</span>
        {isDone && (
          <button onClick={() => toggle(r, false)} data-testid={`soumission-undo-${r.id}`}
            title="Remettre en attente"
            className="shrink-0 text-ivory/40 hover:text-gold transition-colors">
            <RotateCcw size={13} />
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="bg-surface border border-gold/15 rounded-xl p-6 mb-8" data-testid="forfait-soumissions-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[11px] uppercase tracking-widest text-gold inline-flex items-center gap-2">
          <ClipboardList size={12} /> Soumissions de forfaits — commandes en attente
          {pending.length > 0 && (
            <span className="bg-amber-500/20 text-amber-300 rounded-full px-2 py-0.5 normal-case tracking-normal"
              data-testid="soumissions-pending-count">{pending.length} en attente</span>
          )}
        </h3>
        {done.length > 0 && (
          <button onClick={() => setShowDone((v) => !v)} data-testid="soumissions-toggle-done"
            className="text-[11px] text-ivory/50 hover:text-gold underline underline-offset-2 transition-colors">
            {showDone ? "Masquer" : `Voir les traitées (${done.length})`}
          </button>
        )}
      </div>
      {pending.length === 0 ? (
        <p className="text-xs text-ivory/50" data-testid="soumissions-empty">
          Aucune soumission en attente — les demandes du formulaire de la page /forfaits arrivent ici et à info@trophee.ca.
        </p>
      ) : (
        <ul className="space-y-2">{pending.map((r) => <Row key={r.id} r={r} isDone={false} />)}</ul>
      )}
      {showDone && done.length > 0 && (
        <ul className="space-y-2 mt-4 pt-4 border-t border-ivory/10">
          {done.map((r) => <Row key={r.id} r={r} isDone={true} />)}
        </ul>
      )}
    </div>
  );
}
